import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet, apiDelete } from '../services/api';
import {
  Plus,
  Search,
  FolderKanban,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Edit,
} from 'lucide-react';

interface Project {
  id: string;
  name: string;
  code: string | null;
  status: string;
  is_billable: boolean;
  budget: number | null;
  budget_currency: string | null;
}

interface ProjectStats {
  id: string;
  budget_used_percent: number | null;
  total_expenses: number;
}

interface PaginatedResponse {
  items: Project[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const statusColors: Record<string, string> = {
  active: 'bg-green-500/10 text-green-400',
  completed: 'bg-blue-500/10 text-blue-400',
  on_hold: 'bg-yellow-500/10 text-yellow-400',
  cancelled: 'bg-gray-500/10 text-gray-500',
};

function formatCurrency(amount: number, currency = 'ZAR'): string {
  const symbols: Record<string, string> = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || 'R';
  return `${symbol}${amount.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
}

function getBudgetColor(percent: number): string {
  if (percent >= 100) return 'bg-red-500';
  if (percent >= 80) return 'bg-orange-500';
  return 'bg-green-500';
}

function getBudgetTextColor(percent: number): string {
  if (percent >= 100) return 'text-red-400';
  if (percent >= 80) return 'text-orange-400';
  return 'text-green-400';
}

export default function Projects() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectStats, setProjectStats] = useState<Record<string, ProjectStats>>({});
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);

  const page = parseInt(searchParams.get('page') || '1');
  const status = searchParams.get('status') || '';

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('page_size', '20');
      if (search) params.set('search', search);
      if (status) params.set('status', status);

      const data = await apiGet<PaginatedResponse>(`/api/projects?${params}`);
      setProjects(data.items);
      setTotalPages(data.total_pages);
      setTotal(data.total);

      // Fetch stats for projects with budgets
      const projectsWithBudgets = data.items.filter(p => p.budget && p.budget > 0);
      const statsMap: Record<string, ProjectStats> = {};
      await Promise.all(
        projectsWithBudgets.map(async (project) => {
          try {
            const stats = await apiGet<ProjectStats>(`/api/projects/${project.id}/stats`);
            statsMap[project.id] = stats;
          } catch (err) {
            console.error(`Failed to fetch stats for project ${project.id}:`, err);
          }
        })
      );
      setProjectStats(statsMap);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [page, search, status]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleDelete = async (projectId: string) => {
    if (!confirm('Are you sure you want to delete this project?')) return;
    try {
      await apiDelete(`/api/projects/${projectId}`);
      fetchProjects();
    } catch (err) {
      console.error('Failed to delete project:', err);
    }
    setActionMenuId(null);
  };

  const statusFilters = ['', 'active', 'completed', 'on_hold', 'cancelled'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Projects</h1>
          <p className="text-gray-400">{total} total projects</p>
        </div>
        <Link
          to="/admin/projects/new"
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Project
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search projects..."
              className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </form>

        <div className="flex gap-2 flex-wrap">
          {statusFilters.map((s) => (
            <button
              key={s}
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('page', '1');
                if (s) params.set('status', s);
                else params.delete('status');
                setSearchParams(params);
              }}
              className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors ${
                status === s
                  ? 'bg-primary-500 text-white'
                  : 'bg-dark-800 text-gray-400 hover:text-white'
              }`}
            >
              {s.replace('_', ' ') || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full p-8 text-center text-gray-500">Loading...</div>
        ) : projects.length === 0 ? (
          <div className="col-span-full p-8 text-center text-gray-500">No projects found</div>
        ) : (
          projects.map((project) => (
            <div
              key={project.id}
              className="bg-dark-900 rounded-xl border border-dark-800 p-4 relative"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <FolderKanban className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <Link
                      to={`/admin/projects/${project.id}`}
                      className="font-medium text-white hover:text-primary-500"
                    >
                      {project.name}
                    </Link>
                    {project.code && (
                      <p className="text-xs text-gray-500">{project.code}</p>
                    )}
                  </div>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setActionMenuId(actionMenuId === project.id ? null : project.id)}
                    className="p-1 hover:bg-dark-700 rounded"
                  >
                    <MoreHorizontal className="w-4 h-4 text-gray-400" />
                  </button>
                  {actionMenuId === project.id && (
                    <div className="absolute right-0 mt-1 w-36 bg-dark-800 rounded-lg shadow-lg border border-dark-700 z-10">
                      <Link
                        to={`/admin/projects/${project.id}`}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-dark-700"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(project.id)}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-dark-700"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between mb-3">
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                    statusColors[project.status] || statusColors.active
                  }`}
                >
                  {project.status.replace('_', ' ')}
                </span>
                {project.is_billable && (
                  <span className="text-xs text-gray-500">Billable</span>
                )}
              </div>

              {/* Budget Progress */}
              {project.budget && project.budget > 0 && (
                <div className="mt-3 pt-3 border-t border-dark-700">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">Budget</span>
                    {projectStats[project.id]?.budget_used_percent !== undefined && projectStats[project.id]?.budget_used_percent !== null && (
                      <span
                        className={`text-xs font-medium ${getBudgetTextColor(
                          projectStats[project.id].budget_used_percent!
                        )}`}
                      >
                        {projectStats[project.id].budget_used_percent!.toFixed(0)}% used
                      </span>
                    )}
                  </div>
                  <div className="relative h-1.5 bg-dark-700 rounded-full overflow-hidden">
                    {projectStats[project.id]?.budget_used_percent !== undefined && projectStats[project.id]?.budget_used_percent !== null && (
                      <div
                        className={`absolute left-0 top-0 h-full rounded-full transition-all ${getBudgetColor(
                          projectStats[project.id].budget_used_percent!
                        )}`}
                        style={{
                          width: `${Math.min(projectStats[project.id].budget_used_percent!, 100)}%`,
                        }}
                      />
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-500">
                      {formatCurrency(
                        projectStats[project.id]?.total_expenses || 0,
                        project.budget_currency || 'ZAR'
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatCurrency(project.budget, project.budget_currency || 'ZAR')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-400">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('page', (page - 1).toString());
                setSearchParams(params);
              }}
              disabled={page <= 1}
              className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
            >
              <ChevronLeft className="w-5 h-5 text-gray-400" />
            </button>
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('page', (page + 1).toString());
                setSearchParams(params);
              }}
              disabled={page >= totalPages}
              className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
            >
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
